import _get from "lodash/get";
import mixpanel from "mixpanel-browser";
import queryString from "qs";
import BrandclubStoreConfigUtils from "../utils/brandclubStoreConfigUtils.js";
import { AnalyticsTrackingEvent } from "./events.js";
import { track as track$1 } from "./externalTracking.js";
const deferredPromise = () => {
  let resolve = () => void 0;
  let reject = () => void 0;
  let isDone = false;
  const p = new Promise((r, e) => {
    resolve = (v) => {
      isDone = true;
      r(v);
    };
    reject = (err) => {
      isDone = true;
      e(err);
    };
  });
  return { p, resolve, reject, isDone: () => isDone };
};
let isInitialized = false;
const __PROD__ = BrandclubStoreConfigUtils.getAppStage() === "prod";
const DEBUG = !__PROD__ && false;
const currentVisitorTracking = deferredPromise();
function setCurrentVisitorTracking(visitorTracking) {
  if (visitorTracking && visitorTracking.uniqueVisitorId) {
    currentVisitorTracking.resolve(visitorTracking);
  }
}
async function track(eventName, eventProps, options) {
  const { location } = window;
  const params = queryString.parse(location.search, {
    ignoreQueryPrefix: true,
    arrayLimit: 100
  });
  const pathName = _get(location.pathname.split("/"), "1");
  const eventNameForService = `${eventName}`;
  const { brandPixels, ...rest } = eventProps || {};
  const paramsForService = {
    ...rest,
    ...params,
    pathName: pathName == null ? void 0 : pathName.toLowerCase(),
    referrer: document.referrer,
    retailerId: (rest == null ? void 0 : rest.retailerId) || params.rid
  };
  try {
    await track$1(eventNameForService, paramsForService, brandPixels);
  } catch (e) {
    console.error(`Error sending event (${eventNameForService}) to external tracking: ${e.message}`);
  }
  try {
    if (!isInitialized) {
      initializeMixpanel();
    }
    if (isInitialized) {
      if (DEBUG) ;
      const visitorTracking = await currentVisitorTracking.p;
      return new Promise((resolve, reject) => {
        var _a, _b, _c, _d, _e, _f, _g;
        try {
          if (visitorTracking && visitorTracking.fingerprintResult) {
            mixpanel.track(
              eventNameForService,
              {
                ...paramsForService,
                ipAddress: visitorTracking.fingerprintResult.ip,
                timeZone: (_a = visitorTracking.fingerprintResult.ipLocation) == null ? void 0 : _a.timezone,
                location: {
                  city: (_b = visitorTracking.fingerprintResult.ipLocation) == null ? void 0 : _b.city,
                  // The city where the endpoint is located.
                  country: (_d = (_c = visitorTracking.fingerprintResult.ipLocation) == null ? void 0 : _c.country) == null ? void 0 : _d.code,
                  // The two-letter code for the country or region of the endpoint. Specified as an ISO 3166-1 alpha-2 code, such as "US" for the United States.
                  latitude: (_e = visitorTracking.fingerprintResult.ipLocation) == null ? void 0 : _e.latitude,
                  // The latitude of the endpoint location, rounded to one decimal place.
                  longitude: (_f = visitorTracking.fingerprintResult.ipLocation) == null ? void 0 : _f.longitude,
                  // The longitude of the endpoint location, rounded to one decimal place.
                  postalCode: (_g = visitorTracking.fingerprintResult.ipLocation) == null ? void 0 : _g.postalCode
                  // The postal code or zip code of the endpoint.
                }
              },
              {
                send_immediately: options == null ? void 0 : options.sendImmediately
              },
              (response) => {
                if (response === 0 || (response == null ? void 0 : response.status) === 0) {
                  reject(new Error("Mixpanel event not sent"));
                } else {
                  resolve({ eventName: eventNameForService, eventParams: paramsForService });
                }
              }
            );
          } else {
            mixpanel.track(
              eventNameForService,
              paramsForService,
              {
                send_immediately: options == null ? void 0 : options.sendImmediately
              },
              (response) => {
                if (response === 0 || (response == null ? void 0 : response.status) === 0) {
                  reject(new Error("Mixpanel event not sent"));
                } else {
                  resolve({ eventName: eventNameForService, eventParams: paramsForService });
                }
              }
            );
          }
        } catch (e) {
          reject(e);
        }
      });
    } else {
      console.error("mixpanel not initialized");
    }
  } catch (e) {
    console.error(`Error sending event (${eventNameForService}) to Mixpanel: ${e.message}`);
  }
}
function warn(msg, obj, eventName = AnalyticsTrackingEvent.WARNING) {
  if (__PROD__) {
    track(eventName, obj || {});
  } else if (obj) {
    console.warn(msg, obj);
  } else {
    console.warn(msg);
  }
}
function error(msg, obj, eventName = AnalyticsTrackingEvent.ERROR) {
  if (__PROD__) {
    track(eventName, obj ? { ...obj, message: msg } : { message: msg });
  } else if (obj) {
    console.error(msg, obj);
  } else {
    console.error(msg);
  }
}
function panic(msg, obj, eventName = AnalyticsTrackingEvent.RETURN_PANIC) {
  if (__PROD__) {
    track(eventName, obj ? { ...obj, msg } : { msg });
  } else if (obj) {
    console.error(msg, obj);
  }
  throw new Error(msg);
}
const getMixpanelApiHost = () => {
  const nodeEnv = process.env.NODE_ENV;
  if (nodeEnv === "development") {
    return void 0;
  }
  return window.location.origin;
};
function initializeMixpanel(domainOverride) {
  if (__PROD__) {
    mixpanel.init("5f5add70c60a83f55ad7ba6d75faf5db", {
      autocapture: {
        pageview: "full-url",
        input: true,
        submit: true,
        capture_text_content: false,
        click: false,
        scroll: false
      },
      ignore_dnt: true,
      api_host: domainOverride || getMixpanelApiHost(),
      // Use sendBeacon for reliability during page transitions
      transport: "sendBeacon"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    });
  } else {
    mixpanel.init("cde1743169537cd47bc24fe7f0ee14b0", {
      autocapture: {
        pageview: "full-url",
        input: true,
        submit: true,
        capture_text_content: false,
        click: false,
        scroll: false
      },
      ignore_dnt: true,
      api_host: domainOverride || getMixpanelApiHost(),
      // Use sendBeacon for reliability during page transitions
      transport: "sendBeacon"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    });
  }
  isInitialized = true;
}
function isMixpanelInitialized() {
  return isInitialized;
}
function setMixpanelUserIdentity(visitorTracking, user) {
  if (user) {
    const { given_name: firstName, family_name: lastName, email, userId } = user;
    mixpanel.identify(userId);
    mixpanel.register({
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      UniqueVisitorId: visitorTracking == null ? void 0 : visitorTracking.uniqueVisitorId
    });
    mixpanel.people.set({
      $first_name: firstName,
      $last_name: lastName,
      $email: email,
      gender: user.genderPredicted || user.gender
    });
  } else if (visitorTracking == null ? void 0 : visitorTracking.uniqueVisitorId) {
    mixpanel.identify(visitorTracking == null ? void 0 : visitorTracking.uniqueVisitorId);
  }
}
function tombstone(msg, obj = {}) {
  track(`tombstone: ${msg}`, obj);
}
function trackRouteChange(previousLocation, nextLocation) {
  const params = queryString.parse(nextLocation.search, {
    ignoreQueryPrefix: true,
    arrayLimit: 100
  });
  track(AnalyticsTrackingEvent.PAGE_VIEW, {
    ...params,
    currentUrl: nextLocation.pathname,
    previousUrl: previousLocation.pathname
  });
}
const trackError = (url, err) => {
  track(AnalyticsTrackingEvent.ERROR_PAGE, {
    url,
    message: err.message,
    backtrace: err.stack || "",
    lineNumber: err.lineNumber
  });
};
export {
  deferredPromise,
  error,
  initializeMixpanel,
  isMixpanelInitialized,
  panic,
  setCurrentVisitorTracking,
  setMixpanelUserIdentity,
  tombstone,
  track,
  trackError,
  trackRouteChange,
  warn
};
//# sourceMappingURL=AnalyticsService.js.map
